export function decodeChannelData (data) {
    if (typeof data === 'string') return data
    return new TextDecoder().decode(data)
}

export function encodeChannelData (data) {
    return new TextEncoder().encode(JSON.stringify(data))
}

export function createPipeMessage (key, data = {}) {
    return encodeChannelData({ key, data })
}

export function decodePipeMessage (data) {
    try {
        const message = JSON.parse(decodeChannelData(data))
        if (!message || typeof message !== 'object' || typeof message.key !== 'string') return null
        return message
    } catch (_) {
        return null
    }
}

function decodePipeData (data) {
    const decoded = decodeChannelData(data)
    try {
        return JSON.parse(decoded)
    } catch (_) {
        return decoded
    }
}

export function createWelinkAdapter ({ globalObject = window, config }) {
    let instance = null
    let initialized = false
    let initializationPromise = null
    let startOperation = null
    let streamListenerRegistered = false
    let activeAttemptId = 0
    let firstVideoFrameHandled = false
    const listeners = new Map()

    const emitForAttempt = (attemptId, event, payload = {}) => {
        const handlers = listeners.get(event) || []
        handlers.forEach(handler => handler({ attemptId, ...payload }))
    }

    const emit = (event, payload = {}) => {
        emitForAttempt(activeAttemptId, event, payload)
    }

    const registerChannelListener = attemptId => {
        instance.setChannelListener({
            onChannelState (state) {
                emitForAttempt(attemptId, 'channel-state', { state })
            },
            receiveRemoteChannelData (data) {
                const message = decodePipeMessage(data)
                if (message && message.key === 'login') {
                    emitForAttempt(attemptId, 'login-request', {
                        key: message.key,
                        data: message.data
                    })
                }
            },
            receiveRemoteChannelDataWithKey (key, data) {
                if (key === 'login') {
                    emitForAttempt(attemptId, 'login-request', {
                        key,
                        data: decodePipeData(data)
                    })
                }
            }
        })
    }

    const registerListeners = () => {
        if (streamListenerRegistered) return

        const constants = instance.WLCGConstant || {}
        const setConfiguredBitrateRange = () => {
            const bitrateRange = config.bitrateRange
            if (!bitrateRange) return
            if (typeof instance.setBitrateRange !== 'function') {
                emit('bitrate-range', { status: 'unsupported' })
                return
            }
            try {
                instance.setBitrateRange(bitrateRange.lowerLimit, bitrateRange.upperLimit)
                emit('bitrate-range', {
                    status: 'set',
                    lowerLimit: bitrateRange.lowerLimit,
                    upperLimit: bitrateRange.upperLimit
                })
            } catch (error) {
                emit('bitrate-range', {
                    status: 'failed',
                    message: error.message
                })
            }
        }
        instance.setStreamListener({
            onConnectState (type, extraData) {
                emit('connect-state', { type, extraData })
            },
            onDecodeFail (code, message) {
                emit('decode-fail', { code, message })
            },
            onStreamExtraData (type, data) {
                emit('stream-extra-data', { type, data })
            },
            onFirstFrame (frameType) {
                const videoType = constants.FrameType && constants.FrameType.VIDEO
                if (frameType !== videoType || firstVideoFrameHandled) return
                firstVideoFrameHandled = true
                setConfiguredBitrateRange()
                emit('first-frame')
            },
            onStreamStatisticsData (data) {
                emit('statistics', { data })
            },
            onStartGameFail (code, message) {
                emit('start-fail', { code, message })
            },
            onRemoteExit (code, message) {
                emit('remote-exit', { code, message })
            }
        })
        registerChannelListener(activeAttemptId)
        streamListenerRegistered = true
    }

    const ensureInstance = () => {
        if (instance) return instance
        if (!globalObject.WelinkCloudGame) {
            throw new Error('云游戏组件加载失败，请刷新重试')
        }
        instance = new globalObject.WelinkCloudGame({
            id: config.containerId,
            IspUrl: config.IspUrl,
            tenantKey: config.tenantKey,
            lockPoint: true,
            enableClipBoard: true
        })
        registerListeners()
        return instance
    }

    return {
        on (event, handler) {
            const handlers = listeners.get(event) || []
            handlers.push(handler)
            listeners.set(event, handlers)
            return () => listeners.set(event, handlers.filter(item => item !== handler))
        },
        initialize () {
            if (initialized) return Promise.resolve(true)
            if (initializationPromise) return initializationPromise
            const sdk = ensureInstance()
            initializationPromise = Promise.resolve(sdk.init()).then(result => {
                if (!result) {
                    throw new Error('云游戏组件初始化失败，请刷新重试')
                }
                initialized = true
                return true
            }).catch(error => {
                initializationPromise = null
                throw error
            })
            return initializationPromise
        },
        beginAttempt (attemptId) {
            activeAttemptId = attemptId
            firstVideoFrameHandled = false
            registerChannelListener(attemptId)
        },
        endAttempt () {
            activeAttemptId = 0
            firstVideoFrameHandled = false
            if (instance) registerChannelListener(0)
        },
        measureBestNode () {
            const sdk = ensureInstance()
            return new Promise((resolve, reject) => {
                const speedType = sdk.MeasureSpeedConfigEnum && sdk.MeasureSpeedConfigEnum.ONLY_PING
                sdk.getNodeWithGameId(config.gameId, result => {
                    const nodes = Array.isArray(result.nodeResult) ? result.nodeResult : [result.nodeResult]
                    const bestNode = nodes.find(Boolean)
                    if (!bestNode || !bestNode.nodeId) {
                        reject(new Error('未获取到可用的云游戏节点'))
                        return
                    }
                    resolve(bestNode.nodeId)
                }, (code, message) => {
                    const error = new Error(message || '云游戏节点测速失败')
                    error.code = code
                    reject(error)
                }, speedType, true)
            })
        },
        getMediaCodecType () {
            return ensureInstance().getMediaCodecType()
        },
        getBizData () {
            return ensureInstance().getBizData()
        },
        startGame (launchPayload) {
            if (startOperation) {
                return Promise.reject(new Error('上一条云游戏启动流程尚未结束'))
            }
            let result
            try {
                result = ensureInstance().startGame(launchPayload)
            } catch (error) {
                return Promise.reject(error)
            }
            const operation = Promise.resolve(result)
            startOperation = operation
            const clearStartOperation = () => {
                if (startOperation === operation) startOperation = null
            }
            operation.then(clearStartOperation, clearStartOperation)
            return operation
        },
        exitGame () {
            if (!instance || !initialized) return
            const pendingStart = startOperation
            const result = instance.exitGame()
            if (!pendingStart) return result
            return pendingStart.catch(() => {}).then(() => result)
        },
        waitForPendingStart () {
            return startOperation ? startOperation.catch(() => {}) : Promise.resolve()
        },
        sendLoginData (token, adid) {
            ensureInstance().sendDataToGameWithKey(encodeChannelData({ token, adid }), 'logindata')
        },
        getInstance () {
            return instance
        }
    }
}
